import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useApi } from "@pcs/telemetry-api";
import type { TelemetryDetail } from "@/api/telemetry";

interface UseTelemetryDetailResult {
  detail: TelemetryDetail | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function useTelemetryDetail(id: string | undefined): UseTelemetryDetailResult {
  const { api } = useApi();
  const [detail, setDetail] = useState<TelemetryDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    setRefetchCounter((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!id) {
      setDetail(null);
      setIsLoading(false);
      return undefined;
    }

    const numericId = Number(id);
    if (Number.isNaN(numericId)) {
      setDetail(null);
      setError("Invalid telemetry id");
      setIsLoading(false);
      return undefined;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);

    api.api
      .v1TelemetryGet(numericId, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          setDetail(response.data as TelemetryDetail);
        }
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) {
          if (isAxiosError(err)) {
            if (err.response?.status === 404) {
              setError("Telemetry record not found");
            } else {
              setError(
                err.response?.data?.title ??
                  "Failed to load telemetry details",
              );
            }
          } else {
            setError("An unexpected error occurred");
          }
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [api, id, refetchCounter]);

  return { detail, isLoading, error, refetch };
}

export default useTelemetryDetail;
